import {
    getOutlookInstance,
    HxEnums,
    HxObjectID,
    HxSecureString,
    HxSslScheme,
    SessionId,
} from '@outlook/hxcore_web';
import { handleHxObjectLoadResult } from 'hx-object-load-helpers';

interface AccountAddedEventData {
    eventType: 'AccountAdded';
    source: 'AccountUtil';
    email: string;
    webAccountId: string;
    accessToken: string;
    expiresDur: number;
    isAad: boolean;
}

export interface AccessTokenUpdateEventData {
    eventType: 'AccessTokenUpdate';
    source: 'AccountUtil';
    email: string;
    accessToken: string;
    expiresDur: number;
}

type AccountEventData = AccountAddedEventData | AccessTokenUpdateEventData;

function isAccountEventData(data: any): data is AccountEventData {
    return typeof data === 'object' && data.source === 'AccountUtil';
}

async function onWebViewEvent(event: any, sessionId: SessionId) {
    const data = event.data;
    if (!isAccountEventData(data)) {
        return;
    }

    switch (data.eventType) {
        case 'AccountAdded': {
            return handleAccountAddedEventData(data as AccountAddedEventData, sessionId);
        }
        case 'AccessTokenUpdate': {
            await handleAccessTokenUpdateEvent(data as AccessTokenUpdateEventData, sessionId);
        }
    }
}

function handleAccountAddedEventData(data: AccountAddedEventData, sessionId: SessionId) {
    const { email, webAccountId, accessToken, isAad } = data as AccountAddedEventData;
    const accessTokenExpires = new Date(data.expiresDur * 1000);
    getOutlookInstance().actorAPIs.createAccount({
        displayName: email,
        emailAddress: email,
        incomingServerAddress: isAad ? 'outlook.office365.com' : 'm.hotmail.com',
        incomingServerPort: 0,
        incomingServerSslScheme: HxSslScheme.SecureFromStart,
        incomingServerUsername: '',
        incomingServerPassword: new HxSecureString(''),
        webAccountId,
        targetAccountId: HxObjectID.objectIdNil,
        syncDeviceAccountTypeId: HxEnums.HxSyncDeviceAccountType.ActiveSyncExchange,
        authType: HxEnums.HxAccountAuthType.Bearer,
        userDisplayName: email,
        activeSyncDomain: '',
        outgoingServerAddress: '',
        outgoingServerPort: 0,
        outgoingServerSslScheme: HxSslScheme.SecureFromStart,
        outgoingServerUsername: '',
        outgoingServerPassword: new HxSecureString(''),
        outgoingServerAuthenticationRequired: true,
        creationState: HxEnums.HxNewAccountCreateState.ForceCreate,
        type: HxEnums.HxAccountType.HxS,
        exchangeForestType: HxEnums.HxExchangeForestType.Office365,
        skipHxAccountsAppActiveCheck: true,
        accessToken: new HxSecureString(accessToken),
        accessTokenExpiration: accessTokenExpires,
        onPremiseUriForCloudCache: '',
        primaryAccountIdForSharedAccount: HxObjectID.objectIdNil,
    });
}

export async function handleAccessTokenUpdateEvent(
    data: AccessTokenUpdateEventData,
    sessionId: SessionId
) {
    const email = data.email;
    const accessToken = data.accessToken;
    const accessTokenExpires = new Date(data.expiresDur * 1000);

    const root = await getOutlookInstance().activeSet.getRoot(sessionId);
    const accounts = handleHxObjectLoadResult(
        await root.getAccountsSyncingMailOrCalendar(sessionId)
    );
    accounts.items.forEach(account => {
        if (account.emailAddress === email) {
            getOutlookInstance().actorAPIs.updateAccountCredentials(account.objectId, {
                accessTokenData: {
                    accessToken: new HxSecureString(accessToken),
                    accessTokenExpiration: accessTokenExpires,
                },
            });
        }
    });
}

/**
 * Installs lifecycle events for responding to messages from
 * the webview2 host.
 */
export function installAccountEventsHandler(): void {
    if (window?.chrome?.webview) {
        window.chrome.webview.addEventListener('message', async (event: any) => {
            const outlookInstance = getOutlookInstance();
            const sessionId = outlookInstance.activeSet.createSession();
            try {
                await onWebViewEvent(event, sessionId);
            } finally {
                outlookInstance.activeSet.releaseSession(sessionId);
            }
        });
    }
}
